import Service, { type Registry as Services, service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import ENV from 'lumen/config/environment';
import type {
  CentralMappingBreakdown,
  CentralMappingCategory,
  CentralMappingMappingEntry,
  CentralMappingMarketSubscription,
} from 'lumen/models';
import type {
  MappingEntriesRawResponseMeta,
  MappingEntriesResponseMeta,
} from 'lumen/serializers/central-mapping/mapping-entry';
import {
  BREAKDOWNS,
  type CentralMappingMappingEntryExtendedModel,
  PMS_VALUE_TYPES,
  type PmsValueType,
} from 'lumen/utils/central-mapping-constants';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';

/**
 * TEMPORARY mock for the channel breakdown (epic PIN-1206 — Net revenue channel
 * mapping). The channel entitlement, its `central-mapping/breakdown` record, its
 * category hierarchy and its mapping entries are not yet served by the API, so
 * this service fakes them client-side so the Channels card (menu), the channel
 * hierarchy surface and the channel mapping page can be demoed without the
 * backend. Everything is gated behind the `net-revenue-settings` toggle.
 *
 * This is a single, self-contained file: to remove it once the channel API is
 * live, delete this service and its `channelBreakdownMock` uses in
 * `central-mapping.ts` and the two hierarchy controllers (grep
 * `channelBreakdownMock` / `isChannelMockEnabled`). The `net-revenue-settings`
 * toggle and all generic channel wiring stay.
 */

const MOCK_CHANNEL_VALUE_TYPES: PmsValueType[] = [
  PMS_VALUE_TYPES.TRAVEL_AGENT,
  PMS_VALUE_TYPES.SOURCE,
  PMS_VALUE_TYPES.ORIGIN,
];

const MOCK_CHANNEL_CATEGORIES: {
  id: string;
  isFixed: boolean;
  name: string;
  parentId: string | null;
}[] = [
  {
    id: 'chan-hotel-direct',
    name: 'Hotel Direct',
    parentId: null,
    isFixed: true,
  },
  {
    id: 'chan-brand-com',
    name: 'Brand.com',
    parentId: 'chan-hotel-direct',
    isFixed: false,
  },
  {
    id: 'chan-call-center',
    name: 'Call Center',
    parentId: 'chan-hotel-direct',
    isFixed: false,
  },
  {
    id: 'chan-walk-in',
    name: 'Walk In',
    parentId: 'chan-hotel-direct',
    isFixed: false,
  },
  {
    id: 'chan-brand-desktop',
    name: 'Desktop',
    parentId: 'chan-brand-com',
    isFixed: false,
  },
  {
    id: 'chan-brand-mobile',
    name: 'Mobile',
    parentId: 'chan-brand-com',
    isFixed: false,
  },
  { id: 'chan-dds', name: 'DDS', parentId: null, isFixed: true },
];

interface MockMappingRow {
  autoCategoryId: string | null;
  description: string;
  userCategoryId: string | null;
  value: string;
}

// A few rows per value type, referencing the category ids above. A mix of
// unmapped / user-mapped / auto-mapped rows so set-mapping, leave-unmapped and
// auto-map can all be exercised in dev.
const MOCK_CHANNEL_MAPPING_ENTRIES: Partial<
  Record<PmsValueType, MockMappingRow[]>
> = {
  [PMS_VALUE_TYPES.TRAVEL_AGENT]: [
    {
      value: 'EXPEDIA',
      description: 'Expedia Group',
      userCategoryId: null,
      autoCategoryId: 'chan-call-center',
    },
    {
      value: 'BOOKINGCOM',
      description: 'Booking.com',
      userCategoryId: 'chan-dds',
      autoCategoryId: null,
    },
    {
      value: 'HRS',
      description: 'HRS Group',
      userCategoryId: null,
      autoCategoryId: null,
    },
  ],
  [PMS_VALUE_TYPES.SOURCE]: [
    {
      value: 'WEB',
      description: 'Website',
      userCategoryId: 'chan-brand-desktop',
      autoCategoryId: null,
    },
    {
      value: 'MOBILE_APP',
      description: 'Mobile App',
      userCategoryId: null,
      autoCategoryId: 'chan-brand-mobile',
    },
    {
      value: 'PHONE',
      description: 'Phone',
      userCategoryId: null,
      autoCategoryId: null,
    },
  ],
  [PMS_VALUE_TYPES.ORIGIN]: [
    {
      value: 'DIRECT',
      description: 'Direct',
      userCategoryId: 'chan-walk-in',
      autoCategoryId: null,
    },
    {
      value: 'INDIRECT',
      description: 'Indirect',
      userCategoryId: null,
      autoCategoryId: null,
    },
  ],
};

interface MockMappingEntriesResult {
  mappingEntries: CentralMappingMappingEntryExtendedModel[];
  paginationMeta: NonNullable<MappingEntriesResponseMeta['paginationMeta']>;
  savingStatus: MappingEntriesResponseMeta['status'];
}

export default class ChannelBreakdownMockService extends Service {
  @service declare featureToggle: Services['feature-toggle'];
  @service declare store: Services['store'];

  @cached
  get breakdown(): CentralMappingBreakdown {
    const valueTypes = MOCK_CHANNEL_VALUE_TYPES.map((valueType) => {
      this.store.push({
        data: {
          type: 'central-mapping/pms-value-type',
          id: `mock-channel-vt-${valueType}`,
          attributes: { valueType, available: true },
        },
      });
      return {
        type: 'central-mapping/pms-value-type',
        id: `mock-channel-vt-${valueType}`,
      };
    });

    return this.store.push({
      data: {
        type: 'central-mapping/breakdown',
        id: 'mock-channel-breakdown',
        attributes: {
          breakdown: BREAKDOWNS.CHANNEL,
          available: true,
          totalValuesUnmapped: 0,
        },
        relationships: {
          valueTypes: { data: valueTypes },
        },
      },
    });
  }

  get isChannelMockEnabled(): boolean {
    return (
      this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.NET_REVENUE_SETTINGS,
      ) && ENV.environment !== 'test'
    );
  }

  /**
   * Persist the channel hierarchy client-side (no backend yet): drop deleted
   * records and re-push the survivors as canonical (clearing dirty attributes,
   * the same way a server round-trip would). Session-only; resets on reload.
   */
  commitHierarchy(categories: CentralMappingCategory[]): void {
    categories
      .filter((category) => category.isDeleted)
      .forEach((category) => category.unloadRecord());

    categories
      .filter((category) => !category.isDeleted)
      .forEach((category) => {
        this.store.push({
          data: {
            type: 'central-mapping/category',
            id: category.id!,
            attributes: {
              isFixed: category.isFixed,
              name: category.name,
              valueType: category.valueType,
            },
            relationships: {
              parent: {
                data: category.parent
                  ? {
                      type: 'central-mapping/category',
                      id: category.parent.id!,
                    }
                  : null,
              },
            },
          },
        });
        category.updateRelationships();
      });
  }

  /**
   * Persist channel mappings client-side (no save endpoint yet): mark the dirty
   * rows as saved by re-canonicalising their relationships, and return the same
   * `{ data, meta }` envelope the real save would, so callers' re-fetch flow is
   * unchanged. Session-only; resets on reload.
   */
  commitMapping(mappingEntries: CentralMappingMappingEntryExtendedModel[]): {
    data: Record<string, unknown>[];
    meta: MappingEntriesRawResponseMeta;
  } {
    mappingEntries.forEach((entry) => entry.updateRelationships());

    return {
      data: [],
      meta: {
        breakdown: BREAKDOWNS.CHANNEL,
        pagination_meta: {
          count: mappingEntries.length,
          page: 1,
          page_count: 1,
          page_size: 100,
        },
        status: {
          in_progress: false,
          subscriptions_processed: null,
          total_subscriptions_processing: null,
        },
      },
    };
  }

  /**
   * Return the channel hierarchy: the current store state if it exists (so
   * edits made during the session survive a re-read), otherwise a freshly
   * pushed mock tree. Returns the root Group records; callers flatten them into
   * the full three-level hierarchy via the `children` relationship.
   */
  getHierarchy(): CentralMappingCategory[] {
    const existing = this.store
      .peekAll<CentralMappingCategory>('central-mapping/category')
      // `valueType` is typed as PmsValueType, which does not include 'channel';
      // compare as a string until the API/type catches up.
      .filter(
        (category) => (category.valueType as string) === BREAKDOWNS.CHANNEL,
      );

    if (existing.length > 0) {
      return existing.filter((category) => category.depth === 0);
    }

    MOCK_CHANNEL_CATEGORIES.forEach(({ id, name, isFixed, parentId }) => {
      this.store.push({
        data: {
          type: 'central-mapping/category',
          id,
          attributes: {
            name,
            isFixed,
            valueType: BREAKDOWNS.CHANNEL,
          },
          relationships: {
            parent: {
              data: parentId
                ? { type: 'central-mapping/category', id: parentId }
                : null,
            },
          },
        },
      });
    });

    const channelCategories = this.store
      .peekAll<CentralMappingCategory>('central-mapping/category')
      .filter(
        (category) => (category.valueType as string) === BREAKDOWNS.CHANNEL,
      );

    // Capture the relationship baseline now that every parent/child link exists,
    // so the freshly materialised tree isn't reported as unsaved changes by the
    // @dirtyRelationship decorator (which treats records with no stored baseline
    // as dirty).
    channelCategories.forEach((category) => category.updateRelationships());

    return channelCategories.filter((category) => category.depth === 0);
  }

  /**
   * Return the mapping entries for a value type: the current store state if it
   * exists (so saved mappings survive the post-save re-fetch), otherwise freshly
   * pushed mock rows linked to the mocked hierarchy categories. `subscription`
   * is the market subscription to attach for market mapping (undefined for hotel).
   */
  getMappingEntries(
    valueType: PmsValueType,
    subscription?: CentralMappingMarketSubscription,
  ): MockMappingEntriesResult {
    const idPrefix = `chan-entry-${valueType}-`;

    let entries = this.store
      .peekAll<CentralMappingMappingEntry>('central-mapping/mapping-entry')
      .filter((entry) => entry.id?.startsWith(idPrefix) ?? false);

    if (entries.length === 0) {
      const rows = MOCK_CHANNEL_MAPPING_ENTRIES[valueType] ?? [];
      entries = rows.map((row, index) => {
        const entry: CentralMappingMappingEntry = this.store.push({
          data: {
            type: 'central-mapping/mapping-entry',
            id: `${idPrefix}${index}`,
            attributes: {
              value: row.value,
              description: row.description,
              descriptionByPms: '',
              valueType,
            },
            relationships: {
              userCategory: {
                data: row.userCategoryId
                  ? { type: 'central-mapping/category', id: row.userCategoryId }
                  : null,
              },
              autoCategory: {
                data: row.autoCategoryId
                  ? { type: 'central-mapping/category', id: row.autoCategoryId }
                  : null,
              },
            },
          },
        });

        // The market mapping table groups rows by property, so link the market
        // subscription (hotel mapping has none).
        if (subscription) {
          entry.subscription = subscription;
        }

        // Capture the relationship baseline so the freshly materialised row
        // isn't reported as an unsaved change by the @dirtyRelationship decorator
        // (which treats records with no stored baseline as dirty).
        entry.updateRelationships();

        return entry;
      });
    }

    return {
      mappingEntries:
        entries as unknown as CentralMappingMappingEntryExtendedModel[],
      savingStatus: undefined,
      paginationMeta: {
        startIndex: entries.length > 0 ? 1 : 0,
        endIndex: entries.length,
        totalNoOfItems: entries.length,
        pageSize: 100,
        page: 1,
        pageCount: 1,
      },
    };
  }
}

declare module '@ember/service' {
  interface Registry {
    'channel-breakdown-mock': ChannelBreakdownMockService;
  }
}
